import { clsx } from '@alexis/helpers/clsx';
import { translateText } from '@alexis/helpers/translation';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import FlightTimeFilter from '@components/flights/FlightTimeFilter';

import { getSelectedTimeBlocksForGenzoTracking } from '@helpers/flight';

import ArrowIcon from '@icons/arrow_short.svg';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { getLocaleState, getTranslationsState } from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilterCategoryContent.module.scss';

type FlightTimesFilterProps = {
  genzoTrackApplyFilterActionEvent: any;
  genzoTrackApplyFilterSearchView: () => void;
};

const FlightTimesFilter: React.FC<FlightTimesFilterProps> = ({
  genzoTrackApplyFilterActionEvent,
  genzoTrackApplyFilterSearchView,
}) => {
  const {
    departLegArrivalTimeBlocksFilter,
    departLegDepartureTimeBlocksFilter,
    returnLegArrivalTimeBlocksFilter,
    returnLegDepartureTimeBlocksFilter,
    toggleMultiCitySingleFilter,
    activeLeg,
  } = useFlightSearchFilters();

  const { legFilterOptions } = useFlightSearchResults();

  const { isRoundTrip } = useFlightSearchPageParams();

  const [searchParams] = useSearchParams();

  const [legIndexWithArrivalTimeExpanded, setLegIndexWithArrivalTimeExpanded] = useState<number[]>(
    [],
  );

  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const selectedDepartLegDepartureTimeBlocksForActiveLeg: FlightFilterTimeBlock[] =
    (departLegDepartureTimeBlocksFilter.find((item) => item.leg === activeLeg)
      ?.values as FlightFilterTimeBlock[]) || [];

  const selectedDepartLegArrivalTimeBlocksForActiveLeg: FlightFilterTimeBlock[] =
    (departLegArrivalTimeBlocksFilter.find((item) => item.leg === activeLeg)
      ?.values as FlightFilterTimeBlock[]) || [];

  const selectedReturnLegDepartureTimeBlocksForActiveLeg: FlightFilterTimeBlock[] =
    (returnLegDepartureTimeBlocksFilter.find((item) => item.leg === activeLeg)
      ?.values as FlightFilterTimeBlock[]) || [];

  const selectedReturnLegArrivalTimeBlocksForActiveLeg: FlightFilterTimeBlock[] =
    (returnLegArrivalTimeBlocksFilter.find((item) => item.leg === activeLeg)
      ?.values as FlightFilterTimeBlock[]) || [];

  const getQueryParamNamesForFlightTimeFilters = (
    selectedLegAndTime: FlightLegAndTime,
  ): FlightSearchResultSearchParams => {
    switch (selectedLegAndTime) {
      case 'departLegDepartureTime':
        return FlightSearchResultSearchParams.DepartLegDepartureTimeBlocks;
      case 'departLegArrivalTime':
        return FlightSearchResultSearchParams.DepartLegArrivalTimeBlocks;
      case 'returnLegDepartureTime':
        return FlightSearchResultSearchParams.ReturnLegDepartureTimeBlocks;
      case 'returnLegArrivalTime':
        return FlightSearchResultSearchParams.ReturnLegArrivalTimeBlocks;
    }
  };

  const handleFlightTimeBlockFilterToggled = (
    timeBlock: FlightFilterTimeBlock,
    selectedLegAndTime: FlightLegAndTime,
  ) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg,
      value: timeBlock,
      searchParamKey: getQueryParamNamesForFlightTimeFilters(selectedLegAndTime),
    });

    const newFilterParam = newFilter.map((item) => item.values.join(',')).join(':');

    setTimeout(() => {
      if (
        selectedLegAndTime === 'departLegDepartureTime' ||
        selectedLegAndTime === 'returnLegDepartureTime'
      ) {
        genzoTrackApplyFilterActionEvent('filter', 'applied', {
          departuretime_box_selected: getSelectedTimeBlocksForGenzoTracking(
            selectedLegAndTime === 'departLegDepartureTime'
              ? newFilterParam
              : searchParams.get(FlightSearchResultSearchParams.DepartLegDepartureTimeBlocks),
            selectedLegAndTime === 'returnLegDepartureTime'
              ? newFilterParam
              : searchParams.get(FlightSearchResultSearchParams.ReturnLegDepartureTimeBlocks),
            isRoundTrip,
          ),
        });
      } else {
        genzoTrackApplyFilterActionEvent('filter', 'applied', {
          arrivaltime_box_selected: getSelectedTimeBlocksForGenzoTracking(
            selectedLegAndTime === 'departLegArrivalTime'
              ? newFilterParam
              : searchParams.get(FlightSearchResultSearchParams.DepartLegArrivalTimeBlocks),
            selectedLegAndTime === 'returnLegArrivalTime'
              ? newFilterParam
              : searchParams.get(FlightSearchResultSearchParams.ReturnLegArrivalTimeBlocks),
            isRoundTrip,
          ),
        });
      }

      genzoTrackApplyFilterSearchView();
    });
  };

  const isArrivalTimeExpandedForLeg = (legIndex: number) => {
    return legIndexWithArrivalTimeExpanded.includes(legIndex);
  };

  const handleArrivalTimeShowHide = (legIndex: number) => {
    setLegIndexWithArrivalTimeExpanded((prev) => {
      if (prev.includes(legIndex)) {
        return prev.filter((index) => index !== legIndex);
      }

      return [...prev, legIndex];
    });
  };

  return isRoundTrip ? (
    <div className={clsx(styles.categoryContent, styles.flightTime)}>
      {legFilterOptions.map((legFilter, index) => {
        const isDepartLeg = index === 0;

        // <!-- Departure -->
        const selectedDepartureTimes = isDepartLeg
          ? selectedDepartLegDepartureTimeBlocksForActiveLeg
          : selectedReturnLegDepartureTimeBlocksForActiveLeg;
        const departTimeString = isDepartLeg ? 'departLegDepartureTime' : 'returnLegDepartureTime';
        // <!-- ========= -->

        // <!-- Arrival -->
        const selectedArrivalTimes = isDepartLeg
          ? selectedDepartLegArrivalTimeBlocksForActiveLeg
          : selectedReturnLegArrivalTimeBlocksForActiveLeg;
        const arrivalTimeString = isDepartLeg ? 'departLegArrivalTime' : 'returnLegArrivalTime';
        // <!-- ========= -->

        return (
          <div key={legFilter.name}>
            <div className={styles.routeName}>
              {legFilter.departureCity!.name}

              <ArrowIcon className={styles.arrowIcon} />

              {legFilter.arrivalCity!.name}
            </div>

            <div className={clsx(styles.itemHeader, styles.flightTime)}>
              {translateText(
                translations.depart_from,
                locale,
                `${legFilter.departureCity!.name} (${legFilter.departureAirport?.code})`,
              )}
            </div>

            <FlightTimeFilter
              selectedTimeBlocks={selectedDepartureTimes}
              onTimeBlocksToggle={(timeBlock) =>
                handleFlightTimeBlockFilterToggled(timeBlock, departTimeString)
              }
            />

            {!isArrivalTimeExpandedForLeg(index) && (
              <div
                className={styles.showHideArrivalTimeFilter}
                onClick={() => handleArrivalTimeShowHide(index)}
              >
                {translations.landing_time}

                <i className={clsx(styles.chevron, styles.bottom)}></i>
              </div>
            )}

            {isArrivalTimeExpandedForLeg(index) && (
              <>
                <div className={clsx(styles.itemHeader, styles.flightTime)}>
                  {translateText(
                    translations.arrive_in,
                    locale,
                    `${legFilter.arrivalCity!.name} (${legFilter.arrivalAirport?.code})`,
                  )}
                </div>
                <FlightTimeFilter
                  selectedTimeBlocks={selectedArrivalTimes}
                  onTimeBlocksToggle={(timeBlock) =>
                    handleFlightTimeBlockFilterToggled(timeBlock, arrivalTimeString)
                  }
                />
                <div
                  className={styles.showHideArrivalTimeFilter}
                  onClick={() => handleArrivalTimeShowHide(index)}
                >
                  {translations.hide_arrival_time}

                  <i className={clsx(styles.chevron, styles.top)}></i>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  ) : (
    <div className={clsx(styles.categoryContent, styles.flightTime)}>
      <div>
        {legFilterOptions[activeLeg].departureCity?.name &&
          legFilterOptions[activeLeg].arrivalCity?.name && (
            <div className={styles.routeName}>
              {legFilterOptions[activeLeg].departureCity?.name}

              <ArrowIcon className={styles.arrowIcon} />

              {legFilterOptions[activeLeg].arrivalCity?.name}
            </div>
          )}
        <div className={clsx(styles.itemHeader, styles.flightTime)}>
          {translateText(
            translations.depart_from,
            locale,
            `${legFilterOptions[activeLeg].departureCity?.name || ''} (${
              legFilterOptions[activeLeg].departureAirport?.code
            })`,
          )}
        </div>

        <FlightTimeFilter
          selectedTimeBlocks={selectedDepartLegDepartureTimeBlocksForActiveLeg}
          onTimeBlocksToggle={(timeBlock) =>
            handleFlightTimeBlockFilterToggled(timeBlock, 'departLegDepartureTime')
          }
        />

        {!isArrivalTimeExpandedForLeg(activeLeg - 1) && (
          <div
            className={styles.showHideArrivalTimeFilter}
            onClick={() => handleArrivalTimeShowHide(activeLeg - 1)}
          >
            {translations.landing_time}

            <i className={clsx(styles.chevron, styles.bottom)}></i>
          </div>
        )}
        {isArrivalTimeExpandedForLeg(activeLeg - 1) && (
          <>
            <div className={clsx(styles.itemHeader, styles.flightTime)}>
              {translateText(
                translations.arrive_in,
                locale,
                `${legFilterOptions[activeLeg].arrivalCity!.name} (${
                  legFilterOptions[activeLeg].arrivalAirport?.code
                })`,
              )}
            </div>

            <FlightTimeFilter
              selectedTimeBlocks={selectedDepartLegArrivalTimeBlocksForActiveLeg}
              onTimeBlocksToggle={(timeBlock) =>
                handleFlightTimeBlockFilterToggled(timeBlock, 'departLegArrivalTime')
              }
            />

            <div
              className={styles.showHideArrivalTimeFilter}
              onClick={() => handleArrivalTimeShowHide(activeLeg - 1)}
            >
              {translations.hide_arrival_time}

              <i className={clsx(styles.chevron, styles.top)}></i>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FlightTimesFilter;
